import React from 'react';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';
import { Card, CardContent } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import {Heart, Users, Target, MessageCircleDashed as MessageCircle, DollarSign, Play} from 'lucide-react';

export function About() {
  React.useEffect(() => {
    // Add Schema.org markup for About page
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "AboutPage",
      "name": "Sobre o Doe",
      "description": "Um projeto independente construído com cuidado e paixão por conectar quem quer ajudar com quem precisa de ajuda.",
      "mainEntity": {
        "@type": "Organization",
        "name": "HemoConnect",
        "identifier": "63.163.098/0001-18",
        "founder": {
          "@type": "Person",
          "name": "Luan Alysson de Souza",
          "email": "luanalysson@gmail.com"
        },
        "location": {
          "@type": "Place",
          "address": {
            "@type": "PostalAddress",
            "addressLocality": "Juiz de Fora",
            "addressRegion": "MG",
            "addressCountry": "BR"
          }
        }
      }
    });
    document.head.appendChild(script);
    return () => {
      document.head.removeChild(script);
    };
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
      <Navbar />
      
      <div className="container mx-auto px-4 py-16 max-w-6xl">
        {/* Hero Section - Simplified */}
        <div className="text-center mb-20">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-red-600 mb-6 shadow-lg">
            <Heart className="h-8 w-8 text-white fill-current" />
          </div>
          <h1 className="text-5xl md:text-6xl font-black text-gray-900 mb-6 leading-tight">
            Sobre o Doe
          </h1>
          <p className="text-xl md:text-2xl text-gray-600 max-w-3xl mx-auto leading-relaxed font-light">
            Um projeto independente construído com cuidado e paixão por conectar quem quer ajudar com quem precisa de ajuda.
          </p>
        </div>

        {/* Videos Section - MOVED TO TOP */}
        <div className="mb-20">
          <div className="text-center mb-12">
            <div className="inline-flex items-center space-x-2 mb-4">
              <Play className="h-6 w-6 text-red-600" />
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900">
                Conheça o Projeto
              </h2>
            </div>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Veja como o <strong className="text-red-600">Doe</strong> funciona e como você pode salvar vidas
            </p>
          </div>

          {/* Main Video */}
          <div className="mb-8 max-w-4xl mx-auto">
            <div className="relative w-full rounded-2xl overflow-hidden shadow-2xl" style={{ paddingBottom: '56.25%' }}>
              <iframe
                className="absolute top-0 left-0 w-full h-full"
                src="https://www.youtube.com/embed/x7_hZT0aHT0"
                title="Doe - Vídeo Principal"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            </div>
          </div>

          {/* Additional Videos Grid */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {[
              { id: 'jJABP6RIhSc', title: 'Vídeo 1' },
              { id: '8nE_DVqt4E0', title: 'Vídeo 2' },
              { id: '09aHFmBcIM8', title: 'Vídeo 3' },
              { id: 'xudB69Gspm4', title: 'Vídeo 4' },
              { id: 'cchhuRiyYBU', title: 'Vídeo 5' },
              { id: 'Id6vTfGlJlQ', title: 'Vídeo 6' },
              { id: 'KdKzB7xwu6Y', title: 'Vídeo 7' }
            ].map((video) => (
              <div key={video.id} className="relative w-full rounded-xl overflow-hidden shadow-lg hover:shadow-xl transition-shadow" style={{ paddingBottom: '56.25%' }}>
                <iframe
                  className="absolute top-0 left-0 w-full h-full"
                  src={`https://www.youtube.com/embed/${video.id}`}
                  title={`Doe - ${video.title}`}
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              </div>
            ))}
          </div>
        </div>

        {/* Origin Story - Improved Readability */}
        <div className="mb-16 max-w-4xl mx-auto">
          <Card className="border-none shadow-xl bg-white">
            <CardContent className="p-8 md:p-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-6">Nossa História</h2>
              <div className="prose prose-lg max-w-none">
                <p className="text-gray-700 leading-relaxed mb-5 text-lg">
                  <strong className="text-red-600">Doe</strong> nasceu das mãos de um estudante brasileiro apaixonado por tecnologia e saúde, que acredita que <strong>ninguém deveria ficar sozinho quando precisa de sangue</strong>.
                </p>
                <p className="text-gray-700 leading-relaxed mb-5 text-lg">
                  Este app é independente, construído com tempo limitado e muito carinho, para conectar doadores e pessoas que precisam de doação de forma simples, rápida e humana.
                </p>
                <p className="text-gray-700 leading-relaxed text-lg">
                  Ao se cadastrar, doar ou compartilhar, você não só salva vidas: <strong className="text-red-600">você também ajuda esse projeto a continuar existindo</strong>.
                </p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Mission Cards - Modernized */}
        <div className="mb-16 max-w-5xl mx-auto">
          <h2 className="text-3xl font-bold text-gray-900 text-center mb-12">
            Nossos Valores
          </h2>
          <div className="grid md:grid-cols-3 gap-8">
            <Card className="border-none shadow-lg hover:shadow-xl transition-all hover:-translate-y-1 bg-gradient-to-br from-red-50 to-white">
              <CardContent className="p-8 text-center">
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-red-100 mb-6">
                  <Users className="h-8 w-8 text-red-600" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">Pertencimento</h3>
                <p className="text-gray-600 leading-relaxed">
                  Você faz parte de uma rede que se apoia de verdade
                </p>
              </CardContent>
            </Card>

            <Card className="border-none shadow-lg hover:shadow-xl transition-all hover:-translate-y-1 bg-gradient-to-br from-red-50 to-white">
              <CardContent className="p-8 text-center">
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-red-100 mb-6">
                  <Heart className="h-8 w-8 text-red-600" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">Transformação</h3>
                <p className="text-gray-600 leading-relaxed">
                  Você transforma medo em esperança
                </p>
              </CardContent>
            </Card>

            <Card className="border-none shadow-lg hover:shadow-xl transition-all hover:-translate-y-1 bg-gradient-to-br from-red-50 to-white">
              <CardContent className="p-8 text-center">
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-red-100 mb-6">
                  <Target className="h-8 w-8 text-red-600" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">Impacto Real</h3>
                <p className="text-gray-600 leading-relaxed">
                  Você ajuda a reduzir a falta de sangue em hospitais
                </p>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Why Support - Enhanced */}
        <div className="mb-16 max-w-4xl mx-auto">
          <Card className="border-none shadow-xl bg-gradient-to-br from-green-50 via-emerald-50 to-white overflow-hidden">
            <CardContent className="p-8 md:p-12">
              <div className="flex flex-col md:flex-row md:items-start md:space-x-6">
                <div className="flex-shrink-0 mb-6 md:mb-0">
                  <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-green-600 shadow-lg">
                    <DollarSign className="h-8 w-8 text-white" />
                  </div>
                </div>
                <div className="flex-1">
                  <h2 className="text-3xl font-bold text-gray-900 mb-6">
                    Por Que Apoiar o Projeto?
                  </h2>
                  <div className="space-y-4 mb-8">
                    {[
                      'Mantém o app gratuito para sempre, sem anúncios',
                      'Permite adicionar novas funcionalidades pedidas pela comunidade',
                      'Ajuda a cobrir custos de servidor e manutenção',
                      'Mostra que projetos independentes de impacto social importam'
                    ].map((benefit, index) => (
                      <div key={index} className="flex items-start space-x-3">
                        <div className="flex-shrink-0 w-6 h-6 rounded-full bg-green-600 flex items-center justify-center mt-0.5">
                          <span className="text-white text-sm font-bold">✓</span>
                        </div>
                        <p className="text-gray-700 text-lg leading-relaxed">{benefit}</p>
                      </div>
                    ))}
                  </div>
                  <a
                    href="https://link.mercadopago.com.br/apoieocriador"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <Button className="w-full md:w-auto bg-green-600 hover:bg-green-700 text-lg px-8 py-6 shadow-lg hover:shadow-xl transition-all">
                      <DollarSign className="h-5 w-5 mr-2" />
                      Quero Apoiar o Projeto
                    </Button>
                  </a>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Feedback Section - Modernized */}
        <div className="mb-16 max-w-4xl mx-auto">
          <Card className="border-none shadow-xl bg-gradient-to-br from-blue-50 via-indigo-50 to-white">
            <CardContent className="p-8 md:p-12">
              <div className="flex flex-col md:flex-row md:items-start md:space-x-6">
                <div className="flex-shrink-0 mb-6 md:mb-0">
                  <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-blue-600 shadow-lg">
                    <MessageCircle className="h-8 w-8 text-white" />
                  </div>
                </div>
                <div className="flex-1">
                  <h2 className="text-3xl font-bold text-gray-900 mb-4">
                    Ajude a Melhorar o Doe
                  </h2>
                  <p className="text-gray-700 text-lg leading-relaxed mb-4">
                    Tem alguma ideia, encontrou um problema ou quer sugerir uma melhoria? <strong>Sua opinião ajuda a salvar ainda mais vidas.</strong>
                  </p>
                  <p className="text-gray-600 mb-6 leading-relaxed">
                    O que você está achando do app até agora? Sua opinião importa muito para nós.
                  </p>
                  <a href="mailto:luanalysson@gmail.com?subject=Feedback sobre o Doe">
                    <Button variant="outline" className="border-2 border-blue-600 text-blue-600 hover:bg-blue-50 px-6 py-5 text-lg shadow-md hover:shadow-lg transition-all">
                      <MessageCircle className="h-5 w-5 mr-2" />
                      Enviar Feedback
                    </Button>
                  </a>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Footer Info - Cleaner */}
        <div className="text-center pt-8 border-t border-gray-200 max-w-2xl mx-auto">
          <p className="text-gray-600 mb-2 text-lg">
            Desenvolvido com ❤️ por <strong className="text-gray-900">Luan Alysson de Souza</strong>
          </p>
          <p className="text-sm text-gray-500">
            HemoConnect • CNPJ: 63.163.098/0001-18 • Juiz de Fora, MG
          </p>
        </div>
      </div>

      <Footer />
    </div>
  );
}
